import { CandidateReferenceBadge } from "@components/candidates/CandidateReferenceBadge";
import { HowDeterminedButton } from "@components/provenance/HowDeterminedButton";
import { Button } from "@components/shared/Button";
import { ContentViewerOverlay } from "@components/shared/ContentViewerOverlay";
import { DrivePickerButton } from "@components/shared/DrivePickerButton";
import { FileDropZone } from "@components/shared/FileDropZone";
import { RegenerateConfirmDialog } from "@components/shared/RegenerateConfirmDialog";
import { InfoTooltip, TooltipProvider } from "@components/shared/Tooltip";
import {
  ADVANCE_CANDIDATE_SELECTION_STEP,
  AMEND_CANDIDATE_SELECTION_STEP,
  CREATE_INTERVIEW_CONTRIBUTION,
  DELETE_INTERVIEW_CONTRIBUTION,
  MOVE_CANDIDATE_STAGE,
  RE_EXTRACT_ASSESSMENT_FORM_RESPONSE,
  REGENERATE_INTERVIEW_ROUND_SYNTHESIS,
  REOPEN_CANDIDATE_SELECTION_STEP,
  UPDATE_CANDIDATE_SELECTION_STEP,
  UPDATE_INTERVIEW_CONTRIBUTION,
} from "@lib/graphql/mutations";
import { SELECTION_JOURNEY_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import { SanitizedHtml } from "@lib/SanitizedHtml";
import { isInterviewFlavoured, stepDisplayLabel } from "@lib/selectionSteps";
import {
  AlertTriangle,
  Ban,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  ListChecks,
  PauseCircle,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  UserCheck,
  X,
} from "lucide-react";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useMutation, useQuery } from "urql";
import { type AssessmentFormData, AssessmentFormPanel } from "./AssessmentFormPanel";
import { FeedbackNotesEditor, isEmptyNotesHtml } from "./FeedbackNotesEditor";
import { type InterviewGuideData, InterviewGuidePanel } from "./InterviewGuidePanel";
import { type InterviewPackData, InterviewPackPanel } from "./InterviewPackPanel";
import { InterviewsOverviewMatrix } from "./InterviewsOverviewMatrix";
import { StageBadge } from "./StageBadge";
import { TerminalStageMoveControl } from "./StageDropdown";

interface SelectionJourneyStageProps {
  campaignId: string;
}

interface InterviewRoundSynthesisAreaToProbe {
  topic: string;
  rationale?: string;
}

interface InterviewRoundSynthesisEvidenceEntry {
  evidence_text?: string;
  source_excerpt?: string | null;
  source_contribution_id?: string | null;
  source_kind?: string | null;
  contributor_name?: string | null;
}

interface InterviewRoundSynthesisEvidenceGroup {
  capability_dimension_id?: string;
  dimension_name?: string | null;
  in_focus?: boolean;
  depth?: string | null;
  entries?: InterviewRoundSynthesisEvidenceEntry[];
}

interface InterviewRoundSynthesisGap {
  capability_dimension_id?: string;
  dimension_name?: string | null;
  note?: string;
  follow_up_questions?: string[];
}

interface InterviewRoundSynthesisContent {
  round_summary?: string;
  areas_to_probe?: InterviewRoundSynthesisAreaToProbe[];
  evidence?: InterviewRoundSynthesisEvidenceGroup[];
  gaps?: InterviewRoundSynthesisGap[];
  interview_contribution_ids?: string[];
}

interface InterviewRoundSynthesisCompetencyScoreData {
  id: string;
  capabilityDimensionId: string;
  capabilityDimensionContinuityId: string | null;
  dimensionName: string | null;
  score: number;
  evidence: string;
  behaviouralAnchor: string | null;
  anchorLabel: string | null;
}

interface InterviewRoundSynthesisVersionData {
  id: string;
  versionNumber: number;
  generationState: string;
  generationError: string | null;
  changeSummary: string | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  origin?: string | null;
  content: InterviewRoundSynthesisContent;
  author: { id: string; name: string };
  competencyScores?: InterviewRoundSynthesisCompetencyScoreData[];
}

interface InterviewRoundSynthesisData {
  id: string;
  generationState: string | null;
  assessmentState: string;
  staleContributionCount: number;
  totalVersions: number;
  currentVersion: InterviewRoundSynthesisVersionData | null;
  versions?: InterviewRoundSynthesisVersionData[];
}

interface CandidateSelectionStepAmendmentData {
  id: string;
  eventType: string;
  fromOutcome: string;
  toOutcome: string;
  fromRating: number | null;
  toRating: number | null;
  reason: string | null;
  createdAt: string;
  author: { id: string; name: string | null };
}

interface CandidateSelectionStepData {
  id: string;
  continuityId: string | null;
  round: number | null;
  status: string;
  outcome: string;
  rating: number | null;
  stepType: string | null;
  label: string | null;
  candidatePrepNotes: string | null;
  createdAt: string;
  updatedAt: string;
  interviewPackEligible: boolean;
  assessmentFormEligible: boolean;
  amended: boolean;
  outcomeContradictsLaterActivity: boolean;
  amendments: CandidateSelectionStepAmendmentData[];
  assessmentForm: AssessmentFormData | null;
  interviewPack: InterviewPackData | null;
  interviewRoundSynthesis: InterviewRoundSynthesisData | null;
}

interface InterviewContributionData {
  id: string;
  kind: string;
  candidateSelectionStepId: string | null;
  occurredAt: string | null;
  createdAt: string;
  updatedAt: string | null;
  contributor: { id: string; name: string };
  note: {
    interviewType: string;
    rawNotes: string | null;
    durationMinutes: number | null;
    recordingNotes: string | null;
  } | null;
  transcript: {
    content: string | null;
    source: string | null;
    originalFilename: string | null;
    extractionState: string | null;
  } | null;
  assessmentFormSubmission: {
    recommendation: string | null;
    summary: string | null;
    extractionState: string | null;
    extractionError: string | null;
    residualNotes: string | null;
    importedFileUrl: string | null;
    importedFileFilename: string | null;
    reExtractable: boolean;
  } | null;
}

interface CampaignCandidateData {
  id: string;
  stage: string;
  candidate: {
    id: string;
    fullName: string;
    referenceNumber?: number;
    currentRoleTitle: string | null;
    currentCompany: string | null;
  };
  candidateAssessment: { id: string } | null;
  candidateSelectionSteps: CandidateSelectionStepData[];
  interviewContributions: InterviewContributionData[];
  // The reject/hold step the journey halted at (#3168). Null while live.
  selectionJourneyHalt: { step: { id: string }; reason: string } | null;
}

interface JourneyHalt {
  step: CandidateSelectionStepData;
  reason: string;
}

// Plain text of a rich-text note, for prefilling the rejection reason.
function noteToPlainText(html: string): string {
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();
}

const REJECTION_REASON_MAX_LENGTH = 300;

const OUTCOME_COLORS: Record<string, string> = {
  pending: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  advance: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  reject: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  hold: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
};

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  scheduled: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  completed: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
};

const RATING_COLORS = [
  "",
  "text-red-600",
  "text-orange-600",
  "text-amber-600",
  "text-green-600",
  "text-emerald-600",
];

const ADVANCE_OUTCOMES = ["advance", "reject", "hold"];

// The interview-flavoured set (recruiter screening through final round) is
// shared with the selection-process editor via @lib/selectionSteps so both
// surfaces agree on which steps carry an assessment plan / sub-tab.
// `psychometric` is intentionally excluded here: candidates sitting on a
// psychometric step appear under "All candidates" only.

interface SelectionProcessStepData {
  id: string;
  continuityId: string;
  stepType: string | null;
  label: string | null;
  position: number;
}

const ALL_TAB = "all" as const;
type ActiveTab = typeof ALL_TAB | string; // string = step continuityId

const INTERVIEW_NOTE_TYPES = [
  "screening",
  "competency",
  "technical",
  "cultural",
  "final",
  "motivational_fit",
  "general",
];
const INTERVIEW_NOTE_TYPE_LABELS: Record<string, string> = {
  screening: "Screening",
  competency: "Competency",
  technical: "Technical",
  cultural: "Cultural",
  final: "Final",
  motivational_fit: "Motivational fit",
  general: "General",
};
const CONTRIBUTION_KIND_LABELS: Record<string, string> = {
  note: "Note",
  transcript: "Transcript",
  assessment_form_submission: "Assessment Form",
};

type FeedbackMode = "note" | "paste" | "upload";

interface FeedbackForm {
  mode: FeedbackMode;
  interviewType: string;
  rawNotes: string;
  durationMinutes: string;
  pasteContent: string;
  file: File | null;
  driveSignedId: string | null;
  driveFilename: string | null;
  uploading: boolean;
  error: string | null;
}

const DEFAULT_FEEDBACK_FORM: FeedbackForm = {
  mode: "note",
  interviewType: "screening",
  rawNotes: "",
  durationMinutes: "",
  pasteContent: "",
  file: null,
  driveSignedId: null,
  driveFilename: null,
  uploading: false,
  error: null,
};

function getCsrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "";
}

// Story #2317. Candidate-scoped pre-interview notes attached to a selection step.
// Auto-saves with a debounce; the saved value drives interview-pack composition
// and the assessment-form structure, so later, more-personal interviews can be
// tailored without forking the step.
function PreInterviewNotesEditor({
  stepId,
  initialNotes,
  disabled,
}: {
  stepId: string;
  initialNotes: string | null;
  disabled: boolean;
}) {
  const [value, setValue] = useState<string>(initialNotes ?? "");
  const [savedValue, setSavedValue] = useState<string>(initialNotes ?? "");
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [{ fetching }, updateStep] = useMutation(UPDATE_CANDIDATE_SELECTION_STEP);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const next = initialNotes ?? "";
    setValue(next);
    setSavedValue(next);
    setStatus("idle");
  }, [initialNotes]);

  useEffect(() => {
    if (disabled) return;
    if (value === savedValue) return;

    if (timer.current) clearTimeout(timer.current);
    setStatus("saving");
    timer.current = setTimeout(() => {
      updateStep({ input: { id: stepId, candidatePrepNotes: value } }).then((res) => {
        const errs = res.data?.updateCandidateSelectionStep?.errors ?? [];
        if (res.error || errs.length > 0) {
          setStatus("error");
          return;
        }
        setSavedValue(value);
        setStatus("saved");
      });
    }, 400);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [value, savedValue, stepId, disabled, updateStep]);

  const hasNotes = value.trim().length > 0;
  const statusLabel =
    status === "saving" || fetching
      ? "Saving…"
      : status === "saved"
        ? "Saved"
        : status === "error"
          ? "Failed to save"
          : "";

  return (
    <div
      className="mt-3 rounded border border-border bg-card-alt p-3"
      data-testid={`pre-interview-notes-${stepId}`}
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <h5 className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
          <ClipboardList size={10} /> Pre-interview notes
          {hasNotes && (
            <span
              className="ml-1 rounded-full bg-accent/10 px-1.5 py-0.5 text-[9px] font-medium text-accent"
              data-testid={`pre-interview-notes-present-${stepId}`}
            >
              present
            </span>
          )}
        </h5>
        {statusLabel && (
          <span
            className={`text-[10px] ${status === "error" ? "text-red-600 dark:text-red-400" : "text-text-faint"}`}
            data-testid={`pre-interview-notes-status-${stepId}`}
          >
            {statusLabel}
          </span>
        )}
      </div>
      <p className="mb-1 text-[10px] text-text-faint">
        Candidate-specific guidance the interviewer should see in the generated pack and assessment
        form.
      </p>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={disabled}
        rows={3}
        placeholder="e.g. Probe the 2023 exit from Acme — public reports diverge from his account."
        className="w-full resize-y rounded border border-border bg-surface px-2 py-1 text-xs text-text-primary disabled:opacity-50"
        data-testid={`pre-interview-notes-textarea-${stepId}`}
      />
    </div>
  );
}

export function SelectionJourneyStage({ campaignId }: SelectionJourneyStageProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Per-candidate draft of the current step's outcome + rating, keyed by cc id.
  const [recordDraft, setRecordDraft] = useState<
    Record<string, { outcome: string; rating: string }>
  >({});
  // Feedback intake form is per-step; one open at a time.
  const [feedbackStepId, setFeedbackStepId] = useState<string | null>(null);
  const [feedbackForm, setFeedbackForm] = useState<FeedbackForm>(DEFAULT_FEEDBACK_FORM);
  // Open content viewer overlay for a contribution body (note/transcript/summary/residual).
  const [viewer, setViewer] = useState<{
    title: string;
    format: "html" | "text";
    content: string;
    downloadFilename: string;
  } | null>(null);

  const [{ data, fetching }, reexecute] = useQuery({
    query: SELECTION_JOURNEY_QUERY,
    variables: { id: campaignId },
  });
  const [{ fetching: advancing }, advanceStep] = useMutation(ADVANCE_CANDIDATE_SELECTION_STEP);
  const [, moveCandidateStage] = useMutation(MOVE_CANDIDATE_STAGE);
  const [{ fetching: savingFeedback }, createInterviewContribution] = useMutation(
    CREATE_INTERVIEW_CONTRIBUTION,
  );
  const [{ fetching: updatingContribution }, updateInterviewContribution] = useMutation(
    UPDATE_INTERVIEW_CONTRIBUTION,
  );
  const [{ fetching: deletingContribution }, deleteInterviewContribution] = useMutation(
    DELETE_INTERVIEW_CONTRIBUTION,
  );
  const [{ fetching: reopening }, reopenSelectionStep] = useMutation(
    REOPEN_CANDIDATE_SELECTION_STEP,
  );
  const [{ fetching: amending }, amendSelectionStep] = useMutation(AMEND_CANDIDATE_SELECTION_STEP);
  const [{ fetching: reExtractingAssessmentForm }, reExtractAssessmentForm] = useMutation(
    RE_EXTRACT_ASSESSMENT_FORM_RESPONSE,
  );
  const [{ fetching: regenerating }, regenerateSynthesis] = useMutation(
    REGENERATE_INTERVIEW_ROUND_SYNTHESIS,
  );
  // Per-step regenerate-error.
  const [regenerateErrors, setRegenerateErrors] = useState<Record<string, string>>({});
  // Per-step pending regenerate confirm: stepId → warning payload. Shown via
  // RegenerateConfirmDialog when the mutation returns manual_edits_present.
  const [regenerateConfirm, setRegenerateConfirm] = useState<{
    stepId: string;
    warning: string;
    deepLinkPath?: string | null;
    deepLinkLabel?: string | null;
  } | null>(null);
  // Story #2265 / #2293 — loud-failure for step completion. Holds the cc id
  // whose Record click is awaiting confirmation because the current step has
  // no InterviewContributions yet. We allow recording without feedback but
  // make the user confirm — the expected flow is feedback first, outcome
  // second. null when no confirmation is pending.
  const [recordWithoutFeedbackFor, setRecordWithoutFeedbackFor] = useState<string | null>(null);
  // Per-contribution edit state. id → draft body for note (HTML) / transcript
  // (plain text). null = not editing that contribution.
  const [editingContribution, setEditingContribution] = useState<{
    id: string;
    kind: "note" | "transcript";
    body: string;
  } | null>(null);
  // Per-step version-history disclosure state. true = expanded.
  const [versionHistoryOpen, setVersionHistoryOpen] = useState<Record<string, boolean>>({});
  // Per-step amendment-history disclosure state. true = expanded.
  const [amendmentHistoryOpen, setAmendmentHistoryOpen] = useState<Record<string, boolean>>({});
  // Contradiction warnings dismissed for this session.
  const [dismissedContradictions, setDismissedContradictions] = useState<Set<string>>(new Set());
  // Amend-outcome dialog: a correction to a completed step's outcome/rating.
  // Later steps are never touched (contrast reopen). null = closed.
  const [amendDialog, setAmendDialog] = useState<{
    stepId: string;
    stepRound: number | null;
    ccId: string;
    stepLabel: string;
    outcome: string;
    rating: string;
    reason: string;
    error: string | null;
  } | null>(null);
  // Pre-outcome assessment fallback: cc id whose Record click is awaiting
  // user choice because the step's assessment is absent/stale/error. The
  // existing recordWithoutFeedbackFor flow handles the no-contributions case;
  // this flow handles the case where contributions exist but the assessment
  // isn't up to date. null when no fallback is pending.
  const [assessmentFallbackFor, setAssessmentFallbackFor] = useState<string | null>(null);
  // "Reject from campaign" inline confirm on the halt banner (#3168):
  // cc id + editable reason. null = closed.
  const [rejectFromCampaign, setRejectFromCampaign] = useState<{
    ccId: string;
    reason: string;
    error: string | null;
  } | null>(null);

  const allCandidates: CampaignCandidateData[] = data?.campaign?.campaignCandidates ?? [];
  // Story #2252: per-step interview guides. Each guide carries the id of the
  // currently-effective SelectionStep it's tied to, so lookup by step.id is
  // safe and survives step edits via the continuity it's keyed on.
  const interviewGuides: InterviewGuideData[] = data?.campaign?.interviewGuides ?? [];
  const processSteps: SelectionProcessStepData[] =
    data?.campaign?.brief?.selectionProcess?.steps ?? [];
  const blueprintLocked: boolean = data?.campaign?.roleBlueprint?.locked ?? false;
  const activeCandidates = allCandidates.filter((cc) => cc.stage === "interview");
  const movedOnCandidates = allCandidates.filter((cc) => cc.stage !== "interview");
  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useCampaignChannel(campaignId, refresh);

  // Sub-tab strip: "All candidates" plus one tab per interview-flavoured
  // step in the campaign's selection process, in position order. Tab id =
  // step continuityId so deep-links survive across step re-materialisations.
  const stepTabs = useMemo(
    () =>
      [...processSteps]
        .filter((s) => isInterviewFlavoured(s.stepType))
        .sort((a, b) => a.position - b.position),
    [processSteps],
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const stepParam = searchParams.get("step");
  // Fall back to "all" when the param is missing OR points at a continuity
  // that's no longer in the process (stale deep-link, deleted step).
  const activeTab: ActiveTab =
    stepParam && stepTabs.some((s) => s.continuityId === stepParam) ? stepParam : ALL_TAB;

  const setActiveTab = useCallback(
    (next: ActiveTab) => {
      const params = new URLSearchParams(searchParams);
      if (next === ALL_TAB) params.delete("step");
      else params.set("step", next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const orderedSteps = (cc: CampaignCandidateData) =>
    [...cc.candidateSelectionSteps].sort((a, b) => (a.round ?? 0) - (b.round ?? 0));

  // Where the journey halted (#3168): the server names the reject/hold step;
  // resolve it to the step row so the banner can label it and amend it.
  const haltOf = (cc: CampaignCandidateData): JourneyHalt | null => {
    const halt = cc.selectionJourneyHalt;
    if (!halt) return null;
    const step = cc.candidateSelectionSteps.find((s) => s.id === halt.step.id);
    return step ? { step, reason: halt.reason } : null;
  };

  // The recordable step: the lowest incomplete one — none while halted, so a
  // second Reject can never be entered on a step that was never reached.
  const currentStepOf = (cc: CampaignCandidateData) =>
    haltOf(cc) ? null : (orderedSteps(cc).find((s) => s.status !== "completed") ?? null);

  const haltHeading = (halt: JourneyHalt) =>
    `${halt.reason === "hold" ? "On hold" : "Rejected"} at ${stepDisplayLabel(halt.step)}`;

  // Returns a terminal-stage (rejected/withdrawn) candidate to the pipeline.
  const handleMoveStage = async (ccId: string, stage: string) => {
    await moveCandidateStage({ input: { campaignCandidateId: ccId, stage } });
    refresh();
  };

  // "Reject from campaign" on the halt banner: an inline confirm with the
  // reason prefilled from the halting step's latest note, then the ordinary
  // stage move to `rejected`. The decision stays the recruiter's (#2243).
  const openRejectFromCampaign = (
    cc: CampaignCandidateData,
    haltStep: CandidateSelectionStepData,
  ) => {
    const latestNote = cc.interviewContributions
      .filter(
        (c) => c.candidateSelectionStepId === haltStep.id && c.kind === "note" && c.note?.rawNotes,
      )
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
    const noteText = latestNote?.note?.rawNotes ? noteToPlainText(latestNote.note.rawNotes) : "";
    const reason = noteText
      ? noteText.slice(0, REJECTION_REASON_MAX_LENGTH)
      : `Rejected at ${stepDisplayLabel(haltStep)}`;
    setRejectFromCampaign({ ccId: cc.id, reason, error: null });
  };

  const submitRejectFromCampaign = async () => {
    if (!rejectFromCampaign) return;
    const input: Record<string, unknown> = {
      campaignCandidateId: rejectFromCampaign.ccId,
      stage: "rejected",
    };
    const reason = rejectFromCampaign.reason.trim();
    if (reason) input.reason = reason;
    const result = await moveCandidateStage({ input });
    const errors: string[] = result.data?.moveCandidateStage?.errors ?? [];
    if (result.error || errors.length > 0) {
      setRejectFromCampaign(
        (prev) =>
          prev && {
            ...prev,
            error: errors[0] ?? result.error?.message ?? "Could not reject this candidate.",
          },
      );
      return;
    }
    setRejectFromCampaign(null);
    refresh();
  };

  const runRegenerate = async (stepId: string, confirmOverwrite: boolean) => {
    setRegenerateErrors((prev) => {
      const next = { ...prev };
      delete next[stepId];
      return next;
    });
    const result = await regenerateSynthesis({
      input: { candidateSelectionStepId: stepId, confirmOverwrite },
    });
    const payload = result.data?.regenerateInterviewRoundSynthesis;
    const errors: string[] = payload?.errors ?? [];
    if (errors.length > 0) {
      setRegenerateErrors((prev) => ({ ...prev, [stepId]: errors[0] }));
      return;
    }
    if (payload?.status === "manual_edits_present" && !confirmOverwrite) {
      setRegenerateConfirm({
        stepId,
        warning: payload.warning ?? "Manual edits will be replaced.",
        deepLinkPath: payload.deepLinkPath ?? null,
        deepLinkLabel: payload.deepLinkLabel ?? null,
      });
      return;
    }
    refresh();
  };

  const handleRegenerate = (stepId: string) => runRegenerate(stepId, false);

  // executeAdvance: the actual mutation call + cleanup. Invoked either
  // directly (no synthesis risk) or after the user confirms via the
  // record-without-feedback overlay (Story #2265 / #2293).
  const executeAdvance = async (ccId: string) => {
    const draft = recordDraft[ccId] ?? { outcome: "advance", rating: "" };
    const input: Record<string, unknown> = {
      campaignCandidateId: ccId,
      outcome: draft.outcome,
    };
    if (draft.rating) input.rating = parseInt(draft.rating, 10);
    setRecordWithoutFeedbackFor(null);
    await advanceStep({ input });
    setRecordDraft((prev) => {
      const next = { ...prev };
      delete next[ccId];
      return next;
    });
    refresh();
  };

  // requestAdvance: Record-button entry point. The expected flow is feedback
  // first, then outcome — closing a step with no InterviewContributions is
  // unusual, so pause and ask the user to confirm (story #2293, broadening
  // the pack-eligible-only check from #2265). On pack-eligible steps the
  // body also names the synthesis skip; on non-pack-eligible steps it just
  // names the missing-feedback risk. The user can still proceed.
  const requestAdvance = (
    ccId: string,
    currentStep: CandidateSelectionStepData,
    stepContributions: InterviewContributionData[],
  ) => {
    if (stepContributions.length === 0) {
      setRecordWithoutFeedbackFor(ccId);
      return;
    }
    // Pre-outcome assessment fallback: contributions exist but the assessment
    // isn't up to date. Ask the user to Generate first (recommended) or
    // Record outcome anyway. Only gates on PACK-ELIGIBLE steps — psychometric
    // and ad-hoc steps don't produce a per-step assessment, so there's
    // nothing to fall back to. up_to_date and generating pass through;
    // absent / stale / error open the fallback modal so the recruiter sees
    // the (re)assessment before committing.
    if (currentStep.interviewPackEligible) {
      const state = currentStep.interviewRoundSynthesis?.assessmentState ?? "absent";
      if (state === "absent" || state === "stale" || state === "error") {
        setAssessmentFallbackFor(ccId);
        return;
      }
    }
    void executeAdvance(ccId);
  };

  const openFeedbackFor = (stepId: string) => {
    if (feedbackStepId === stepId) {
      setFeedbackStepId(null);
      return;
    }
    setFeedbackStepId(stepId);
    setFeedbackForm(DEFAULT_FEEDBACK_FORM);
  };

  const closeFeedback = () => {
    setFeedbackStepId(null);
    setFeedbackForm(DEFAULT_FEEDBACK_FORM);
  };

  const handleSaveFeedback = async (ccId: string, stepId: string) => {
    const base: Record<string, unknown> = {
      campaignCandidateId: ccId,
      candidateSelectionStepId: stepId,
    };

    if (feedbackForm.mode === "note") {
      const input: Record<string, unknown> = {
        ...base,
        kind: "note",
        interviewType: feedbackForm.interviewType,
      };
      if (!isEmptyNotesHtml(feedbackForm.rawNotes)) input.rawNotes = feedbackForm.rawNotes;
      if (feedbackForm.durationMinutes) {
        input.durationMinutes = parseInt(feedbackForm.durationMinutes, 10);
      }
      await createInterviewContribution({ input });
    } else if (feedbackForm.mode === "paste") {
      if (!feedbackForm.pasteContent.trim()) {
        setFeedbackForm((prev) => ({ ...prev, error: "Paste some transcript text first." }));
        return;
      }
      await createInterviewContribution({
        input: {
          ...base,
          kind: "transcript",
          source: "paste",
          content: feedbackForm.pasteContent,
        },
      });
    } else {
      const file = feedbackForm.file;
      const driveSignedId = feedbackForm.driveSignedId;
      if (!file && !driveSignedId) {
        setFeedbackForm((prev) => ({ ...prev, error: "Choose a file to upload." }));
        return;
      }
      setFeedbackForm((prev) => ({ ...prev, uploading: true, error: null }));
      try {
        let signedId: string;
        let originalFilename: string;
        if (driveSignedId) {
          signedId = driveSignedId;
          originalFilename = feedbackForm.driveFilename ?? "transcript";
        } else {
          const formData = new FormData();
          formData.append("file", file as File);
          const response = await fetch("/uploads", {
            method: "POST",
            headers: { "X-CSRF-Token": getCsrfToken() },
            body: formData,
          });
          if (!response.ok) {
            throw new Error(`Upload failed (${response.status})`);
          }
          const uploadData = (await response.json()) as { signed_id: string; filename: string };
          signedId = uploadData.signed_id;
          originalFilename = uploadData.filename;
        }
        await createInterviewContribution({
          input: {
            ...base,
            kind: "transcript",
            fileSignedId: signedId,
            originalFilename,
          },
        });
      } catch (e) {
        setFeedbackForm((prev) => ({
          ...prev,
          uploading: false,
          error: e instanceof Error ? e.message : "Upload failed",
        }));
        return;
      }
    }

    closeFeedback();
    refresh();
  };

  if (fetching && allCandidates.length === 0) {
    return <p className="text-sm text-text-muted">Loading interviews...</p>;
  }

  const handleReExtractAssessmentForm = async (interviewContributionId: string) => {
    if (
      !window.confirm(
        "Re-run the LLM extraction? This replaces the structured fields and residual notes. The original file is preserved.",
      )
    ) {
      return;
    }
    await reExtractAssessmentForm({ input: { interviewContributionId } });
    refresh();
  };

  const startEditContribution = (c: InterviewContributionData) => {
    if (c.kind === "note") {
      setEditingContribution({ id: c.id, kind: "note", body: c.note?.rawNotes ?? "" });
    } else if (c.kind === "transcript") {
      setEditingContribution({
        id: c.id,
        kind: "transcript",
        body: c.transcript?.content ?? "",
      });
    }
  };

  const cancelEditContribution = () => setEditingContribution(null);

  const saveEditContribution = async () => {
    if (!editingContribution) return;
    const { id, kind, body } = editingContribution;
    const input: Record<string, unknown> = { id };
    if (kind === "note") input.rawNotes = body;
    else input.content = body;
    const result = await updateInterviewContribution({ input });
    const errors: string[] = result.data?.updateInterviewContribution?.errors ?? [];
    if (errors.length > 0) {
      window.alert(errors.join("\n"));
      return;
    }
    setEditingContribution(null);
    refresh();
  };

  const handleDeleteContribution = async (c: InterviewContributionData) => {
    if (!window.confirm("Delete this feedback? This cannot be undone.")) return;
    const result = await deleteInterviewContribution({ input: { id: c.id } });
    const errors: string[] = result.data?.deleteInterviewContribution?.errors ?? [];
    if (errors.length > 0) {
      window.alert(errors.join("\n"));
      return;
    }
    refresh();
  };

  const openAmendDialog = (
    step: CandidateSelectionStepData,
    cc: CampaignCandidateData,
    prefilledOutcome?: string,
  ) =>
    setAmendDialog({
      stepId: step.id,
      stepRound: step.round ?? null,
      ccId: cc.id,
      stepLabel: stepDisplayLabel(step),
      outcome: prefilledOutcome ?? step.outcome,
      rating: step.rating != null ? String(step.rating) : "",
      reason: "",
      error: null,
    });

  const submitAmend = async () => {
    if (!amendDialog) return;
    const input: Record<string, unknown> = {
      id: amendDialog.stepId,
      outcome: amendDialog.outcome,
    };
    if (amendDialog.rating) input.rating = parseInt(amendDialog.rating, 10);
    if (amendDialog.reason.trim()) input.reason = amendDialog.reason.trim();
    const result = await amendSelectionStep({ input });
    const payload = result.data?.amendCandidateSelectionStep;
    if (result.error || payload?.status !== "ok") {
      const errors: string[] = payload?.errors ?? [];
      setAmendDialog(
        (prev) => prev && { ...prev, error: errors[0] ?? "Could not amend this step." },
      );
      return;
    }
    setAmendDialog(null);
    refresh();
  };

  const handleReopenStep = async (step: CandidateSelectionStepData) => {
    const result = await reopenSelectionStep({ input: { id: step.id } });
    const payload = result.data?.reopenCandidateSelectionStep;
    if (payload?.status === "ok") {
      refresh();
    }
  };

  const renderContribution = (c: InterviewContributionData) => {
    const typeLabel =
      c.kind === "note" && c.note
        ? (INTERVIEW_NOTE_TYPE_LABELS[c.note.interviewType] ?? c.note.interviewType)
        : (CONTRIBUTION_KIND_LABELS[c.kind] ?? c.kind);
    const transcriptExtractionState = c.transcript?.extractionState ?? null;
    const isTranscriptExtracting =
      c.kind === "transcript" &&
      (transcriptExtractionState === "pending" || transcriptExtractionState === "extracting");
    const transcriptExtractionFailed =
      c.kind === "transcript" && transcriptExtractionState === "failed";
    const afs = c.assessmentFormSubmission;
    const isAfsExtracting =
      c.kind === "assessment_form_submission" &&
      (afs?.extractionState === "pending" || afs?.extractionState === "extracting");
    const afsExtractionFailed =
      c.kind === "assessment_form_submission" && afs?.extractionState === "failed";
    const noteBody = c.note?.rawNotes ?? null;
    const plainBody = c.transcript?.content ?? afs?.summary ?? null;
    const occurredDate = new Date(c.occurredAt ?? c.createdAt).toLocaleDateString();
    const isEditing = editingContribution?.id === c.id;
    const isEditable = c.kind === "note" || c.kind === "transcript";
    const wasEdited =
      c.updatedAt != null &&
      c.createdAt != null &&
      new Date(c.updatedAt).getTime() - new Date(c.createdAt).getTime() > 2000;
    return (
      <div key={c.id} className="rounded border border-border bg-card-alt p-3">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
            {typeLabel}
          </span>
          {wasEdited && (
            <span
              data-testid={`contribution-edited-${c.id}`}
              className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-text-faint dark:bg-gray-800"
            >
              edited
            </span>
          )}
          {c.transcript?.originalFilename && (
            <span className="text-[10px] text-text-faint">{c.transcript.originalFilename}</span>
          )}
          {afs?.importedFileFilename && (
            <span className="text-[10px] text-text-faint">
              {afs.importedFileUrl ? (
                <a
                  href={afs.importedFileUrl}
                  className="underline hover:text-text-secondary"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="assessment-form-import-file-link"
                >
                  {afs.importedFileFilename}
                </a>
              ) : (
                afs.importedFileFilename
              )}
            </span>
          )}
          {(isTranscriptExtracting || isAfsExtracting) && (
            <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              Extracting…
            </span>
          )}
          {(transcriptExtractionFailed || afsExtractionFailed) && (
            <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
              Extraction failed
            </span>
          )}
          {c.note?.durationMinutes && (
            <span className="text-[10px] text-text-faint">{c.note.durationMinutes} min</span>
          )}
          <span className="text-[10px] text-text-faint">
            {c.contributor.name} &middot;{" "}
            {new Date(c.occurredAt ?? c.createdAt).toLocaleDateString()}
          </span>
          {c.kind === "assessment_form_submission" && afs?.reExtractable && (
            <button
              type="button"
              onClick={() => handleReExtractAssessmentForm(c.id)}
              disabled={reExtractingAssessmentForm || isAfsExtracting}
              data-testid="re-extract-assessment-form"
              className="ml-auto rounded border border-border px-2 py-0.5 text-[10px] text-text-muted hover:bg-card-hover disabled:opacity-50"
            >
              Re-extract
            </button>
          )}
          {isEditable && !isEditing && (
            <div className="ml-auto flex items-center gap-1">
              <button
                type="button"
                onClick={() => startEditContribution(c)}
                data-testid={`edit-contribution-${c.id}`}
                className="rounded border border-border px-2 py-0.5 text-[10px] text-text-muted hover:bg-card-hover"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => handleDeleteContribution(c)}
                disabled={deletingContribution}
                data-testid={`delete-contribution-${c.id}`}
                className="rounded border border-border px-2 py-0.5 text-[10px] text-red-600 hover:bg-red-50 disabled:opacity-50 dark:hover:bg-red-900/20"
              >
                Delete
              </button>
            </div>
          )}
        </div>
        {afsExtractionFailed && afs?.extractionError && (
          <p className="text-[10px] text-red-600 dark:text-red-400">{afs.extractionError}</p>
        )}
        {isEditing && editingContribution?.kind === "note" && (
          <div className="space-y-2" data-testid={`edit-contribution-editor-${c.id}`}>
            <FeedbackNotesEditor
              value={editingContribution.body}
              onChange={(html) =>
                setEditingContribution((prev) => (prev ? { ...prev, body: html } : prev))
              }
            />
            <div className="flex justify-end gap-1">
              <button
                type="button"
                onClick={cancelEditContribution}
                className="rounded border border-border px-2 py-1 text-[10px] text-text-muted hover:bg-card-hover"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEditContribution}
                disabled={updatingContribution}
                data-testid={`save-contribution-${c.id}`}
                className="rounded bg-accent px-2 py-1 text-[10px] text-white hover:bg-accent/90 disabled:opacity-50"
              >
                {updatingContribution ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}
        {isEditing && editingContribution?.kind === "transcript" && (
          <div className="space-y-2" data-testid={`edit-contribution-editor-${c.id}`}>
            <textarea
              aria-label="Transcript text"
              value={editingContribution.body}
              onChange={(e) =>
                setEditingContribution((prev) => (prev ? { ...prev, body: e.target.value } : prev))
              }
              rows={6}
              className="w-full rounded border border-border bg-surface px-3 py-2 text-xs"
            />
            <div className="flex justify-end gap-1">
              <button
                type="button"
                onClick={cancelEditContribution}
                className="rounded border border-border px-2 py-1 text-[10px] text-text-muted hover:bg-card-hover"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEditContribution}
                disabled={updatingContribution}
                data-testid={`save-contribution-${c.id}`}
                className="rounded bg-accent px-2 py-1 text-[10px] text-white hover:bg-accent/90 disabled:opacity-50"
              >
                {updatingContribution ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}
        {!isEditing && noteBody && (
          <div className="space-y-1">
            <SanitizedHtml
              className="prose prose-sm dark:prose-invert line-clamp-3 max-w-none text-xs text-text-secondary [mask-image:linear-gradient(to_bottom,black_70%,transparent)]"
              html={noteBody}
            />
            <button
              type="button"
              onClick={() =>
                setViewer({
                  title: `${typeLabel} — ${c.contributor.name} · ${occurredDate}`,
                  format: "html",
                  content: noteBody,
                  downloadFilename: "note.txt",
                })
              }
              data-testid={`view-full-note-${c.id}`}
              className="rounded border border-border px-2 py-0.5 text-[10px] text-text-muted hover:bg-card-hover"
            >
              View full
            </button>
          </div>
        )}
        {!isEditing && plainBody && (
          <div className="space-y-1">
            <p className="line-clamp-3 whitespace-pre-wrap text-xs text-text-secondary [mask-image:linear-gradient(to_bottom,black_70%,transparent)]">
              {plainBody}
            </p>
            <button
              type="button"
              onClick={() =>
                setViewer({
                  title: `${typeLabel} — ${c.contributor.name} · ${occurredDate}`,
                  format: "text",
                  content: plainBody,
                  downloadFilename:
                    c.kind === "transcript"
                      ? (c.transcript?.originalFilename ?? "transcript.txt")
                      : "summary.txt",
                })
              }
              data-testid={`view-full-plain-${c.id}`}
              className="rounded border border-border px-2 py-0.5 text-[10px] text-text-muted hover:bg-card-hover"
            >
              View full
            </button>
          </div>
        )}
        {c.kind === "assessment_form_submission" && afs?.residualNotes && (
          <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 dark:border-amber-700 dark:bg-amber-900/20">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
              Residual content
            </div>
            <div className="space-y-1">
              <p
                className="line-clamp-3 whitespace-pre-wrap text-xs text-amber-800 dark:text-amber-300 [mask-image:linear-gradient(to_bottom,black_70%,transparent)]"
                data-testid="assessment-form-residual-notes"
              >
                {afs.residualNotes}
              </p>
              <button
                type="button"
                onClick={() => {
                  const residual = afs.residualNotes ?? "";
                  setViewer({
                    title: `Residual content — ${c.contributor.name} · ${occurredDate}`,
                    format: "text",
                    content: residual,
                    downloadFilename: "residual-notes.txt",
                  });
                }}
                data-testid={`view-full-residual-${c.id}`}
                className="rounded border border-amber-300 bg-white/60 px-2 py-0.5 text-[10px] text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:bg-transparent dark:text-amber-300 dark:hover:bg-amber-900/30"
              >
                View full
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderFeedbackForm = (ccId: string, stepId: string) => {
    const setMode = (mode: FeedbackMode) =>
      setFeedbackForm((prev) => ({ ...prev, mode, error: null }));

    return (
      <div className="mt-2 rounded border border-border bg-surface p-3 space-y-2">
        <div className="flex gap-1 text-[10px]">
          {(["note", "paste", "upload"] as FeedbackMode[]).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setMode(mode)}
              className={`rounded px-2 py-1 ${
                feedbackForm.mode === mode
                  ? "bg-accent text-white"
                  : "border border-input-border text-text-secondary hover:bg-card-hover"
              }`}
            >
              {mode === "note" ? "Note" : mode === "paste" ? "Paste transcript" : "Upload file"}
            </button>
          ))}
        </div>

        {feedbackForm.mode === "note" && (
          <>
            <div className="flex gap-2">
              <select
                aria-label="Interview type"
                value={feedbackForm.interviewType}
                onChange={(e) =>
                  setFeedbackForm((prev) => ({ ...prev, interviewType: e.target.value }))
                }
                className="rounded border border-input-border bg-card px-2 py-1 text-xs text-text-primary"
              >
                {INTERVIEW_NOTE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {INTERVIEW_NOTE_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
              <input
                type="number"
                aria-label="Duration in minutes"
                value={feedbackForm.durationMinutes}
                onChange={(e) =>
                  setFeedbackForm((prev) => ({ ...prev, durationMinutes: e.target.value }))
                }
                placeholder="Duration (min)"
                className="w-32 rounded border border-input-border bg-card px-2 py-1 text-xs text-text-primary placeholder:text-text-muted"
              />
            </div>
            <FeedbackNotesEditor
              value={feedbackForm.rawNotes}
              onChange={(html) => setFeedbackForm((prev) => ({ ...prev, rawNotes: html }))}
            />
          </>
        )}

        {feedbackForm.mode === "paste" && (
          <textarea
            aria-label="Transcript text"
            value={feedbackForm.pasteContent}
            onChange={(e) =>
              setFeedbackForm((prev) => ({ ...prev, pasteContent: e.target.value, error: null }))
            }
            placeholder="Paste the interview transcript here..."
            rows={6}
            className="w-full rounded border border-input-border bg-card px-3 py-2 text-xs text-text-primary placeholder:text-text-muted"
          />
        )}

        {feedbackForm.mode === "upload" && (
          <FileDropZone
            accept=".txt,.md,.pdf,.docx,.csv,.xlsx,text/plain,text/markdown,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            disabled={feedbackForm.uploading}
            onFiles={(files) =>
              setFeedbackForm((prev) => ({
                ...prev,
                file: files[0] ?? null,
                driveSignedId: null,
                driveFilename: null,
                error: null,
              }))
            }
            overlayLabel="Drop transcript here"
            inputAriaLabel="Transcript file"
          >
            {({ openPicker }) => (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={openPicker}
                    disabled={feedbackForm.uploading}
                    className="rounded border border-input-border bg-card px-2 py-1 text-xs text-text-secondary hover:bg-card-hover disabled:opacity-50"
                  >
                    {feedbackForm.file || feedbackForm.driveSignedId
                      ? "Change file"
                      : "Choose file"}
                  </button>
                  <DrivePickerButton
                    accept=".txt,.md,.pdf,.docx,.csv,.xlsx,text/plain,text/markdown,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    disabled={feedbackForm.uploading}
                    onSignedId={(signedId, filename) =>
                      setFeedbackForm((prev) => ({
                        ...prev,
                        file: null,
                        driveSignedId: signedId,
                        driveFilename: filename,
                        error: null,
                      }))
                    }
                  />
                  <span className="text-[10px] text-text-faint">
                    {feedbackForm.file?.name ??
                      feedbackForm.driveFilename ??
                      "No file chosen — or drop one here"}
                  </span>
                </div>
                <p className="text-[10px] text-text-faint">
                  .txt / .md / .pdf / .docx / .csv / .xlsx — text is extracted in the background.
                </p>
              </div>
            )}
          </FileDropZone>
        )}

        {feedbackForm.error && (
          <p className="text-[10px] text-red-600 dark:text-red-400">{feedbackForm.error}</p>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => handleSaveFeedback(ccId, stepId)}
            disabled={savingFeedback || feedbackForm.uploading}
            className="rounded bg-accent px-3 py-1 text-xs text-white hover:bg-accent/90 disabled:opacity-50"
          >
            {savingFeedback || feedbackForm.uploading ? "Saving..." : "Save"}
          </button>
          <button
            type="button"
            onClick={closeFeedback}
            className="rounded border border-input-border px-3 py-1 text-xs text-text-secondary hover:bg-card-hover"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // Story #2265 / #2293 — pre-completion warning. Shown when the recruiter
  // clicks Record on a current step with no contributions. Two options: stay
  // and add feedback (Cancel), or complete the step anyway. On pack-eligible
  // steps the body also names the synthesis skip; on other step types it
  // just names the missing-feedback risk.
  const renderRecordWithoutFeedbackConfirm = (
    cc: CampaignCandidateData,
    currentStep: CandidateSelectionStepData,
  ) => (
    <div
      role="dialog"
      aria-label="Complete without feedback"
      data-testid="record-without-feedback-confirm"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-md rounded border border-border bg-card p-4 shadow-lg">
        <h3 className="mb-2 text-sm font-semibold text-text-primary">
          Record outcome without feedback?
        </h3>
        <p className="text-xs text-text-secondary">
          {currentStep.interviewPackEligible
            ? "No feedback has been added for this step yet. Recording now will close the step and skip the analysis. The expected flow is to add feedback first, then record the outcome."
            : "No feedback has been added for this step yet. The expected flow is to add feedback first, then record the outcome. You can still record now if you want to."}
        </p>
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setRecordWithoutFeedbackFor(null)}
            className="rounded border border-border bg-card px-3 py-1 text-xs text-text-secondary hover:bg-card-alt"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="confirm-record-without-feedback"
            onClick={() => executeAdvance(cc.id)}
            disabled={advancing}
            className="rounded bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
          >
            {advancing ? "Recording..." : "Record anyway"}
          </button>
        </div>
      </div>
    </div>
  );

  // Pre-outcome fallback: contributions exist but the assessment is absent,
  // stale, or errored. Modal offers "Generate first" (recommended) or
  // "Record outcome anyway". The latter falls back to the server-side
  // post-advance enqueue so a step always ends up with an assessment.
  // Distinct from renderRecordWithoutFeedbackConfirm, which handles the
  // "no contributions at all" case.
  const renderAssessmentFallbackConfirm = (
    cc: CampaignCandidateData,
    currentStep: CandidateSelectionStepData,
  ) => {
    const state = currentStep.interviewRoundSynthesis?.assessmentState ?? "absent";
    const titleByState: Record<string, string> = {
      absent: "Feedback hasn't been analysed yet",
      stale: "Analysis is out of date",
      error: "Last analysis failed",
    };
    const title = titleByState[state] ?? "Analysis isn't up to date";
    return (
      <div
        role="dialog"
        aria-label="Analysis up to date?"
        data-testid="assessment-fallback-confirm"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      >
        <div className="w-full max-w-md rounded border border-border bg-card p-4 shadow-lg">
          <h3 className="mb-2 text-sm font-semibold text-text-primary">{title}</h3>
          <p className="text-xs text-text-secondary">
            Analyse the feedback first so it can inform the outcome, or record the outcome anyway
            and let the analysis run in the background.
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setAssessmentFallbackFor(null)}
              className="rounded border border-border bg-card px-3 py-1 text-xs text-text-secondary hover:bg-card-alt"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="record-outcome-anyway"
              onClick={() => {
                setAssessmentFallbackFor(null);
                void executeAdvance(cc.id);
              }}
              disabled={advancing}
              className="rounded border border-border bg-card px-3 py-1 text-xs text-text-primary hover:bg-card-alt disabled:opacity-50"
            >
              {advancing ? "Recording…" : "Record outcome anyway"}
            </button>
            <button
              type="button"
              data-testid="generate-assessment-first"
              onClick={() => {
                setAssessmentFallbackFor(null);
                void handleRegenerate(currentStep.id);
              }}
              disabled={regenerating}
              className="rounded bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
            >
              {regenerating ? "Starting…" : "Analyse first"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Pre-outcome assessment CTA (Story 2289). Visible whenever a pack-eligible
  // step has at least one InterviewContribution but no synthesis yet — both
  // before the recruiter records the outcome (so they can review the
  // assessment first) and the original post-completion recovery case
  // (advance via "Record outcome anyway" + later add feedback). The
  // error/generating cards in renderSynthesis already provide a Retry button
  // for the post-failure case, so we only render this when no continuity
  // exists yet.
  const renderGenerateSynthesisCTA = (
    step: CandidateSelectionStepData,
    stepContributions: InterviewContributionData[],
  ) => {
    if (!step.interviewPackEligible) return null;
    if (step.interviewRoundSynthesis) return null;
    if (stepContributions.length === 0) return null;

    const error = regenerateErrors[step.id];
    return (
      <div
        data-testid="generate-synthesis-cta"
        className="mt-3 rounded border border-dashed border-purple-300 bg-purple-50/40 p-3 text-xs text-purple-800 dark:border-purple-900/40 dark:bg-purple-900/10 dark:text-purple-200"
      >
        <p>
          {step.status === "completed"
            ? "This step has feedback but no analysis was generated. Run one now to refresh the candidate report."
            : "Analyse this step's feedback to inform the outcome decision."}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-testid={`generate-assessment-${step.id}`}
            onClick={() => handleRegenerate(step.id)}
            disabled={regenerating}
            className="rounded bg-purple-600 px-3 py-1 text-[11px] font-medium text-white hover:bg-purple-700 disabled:opacity-50"
          >
            {regenerating ? "Analysing…" : "Analyse feedback"}
          </button>
          {error && <span className="text-[10px] text-red-600 dark:text-red-400">{error}</span>}
        </div>
      </div>
    );
  };

  const renderSynthesis = (
    step: CandidateSelectionStepData,
    cc: CampaignCandidateData,
    _stepContributions: InterviewContributionData[],
  ) => {
    const synthesis = step.interviewRoundSynthesis;
    if (!synthesis) return null;
    const version = synthesis.currentVersion;
    const state = version?.generationState ?? synthesis.generationState;
    const content = version?.content ?? {};
    // Staleness is server-derived (InterviewRoundSynthesis#assessment_state):
    // a contribution added or edited after the current version was stamped
    // makes the assessment stale, and the count of such contributions powers
    // the banner copy. Auto-regenerating would burn premium-tier LLM tokens
    // silently, so the banner is the loud signal asking the user to refresh.
    const isStale = synthesis.assessmentState === "stale";
    const staleCount = synthesis.staleContributionCount;
    const scoredCells = version?.competencyScores ?? [];

    if (state === "generating") {
      return (
        <div className="mt-3 flex items-start justify-between gap-2 rounded border border-blue-200 bg-blue-50 p-3 text-xs text-blue-700 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-300">
          <span>Analysing…</span>
          {/* Recovery affordance: if a synthesis job crashed (NoMethodError
              etc.) the version can be stranded in "generating". Allow a
              manual retry so users aren't forced to escalate to console. */}
          <button
            type="button"
            onClick={() => handleRegenerate(step.id)}
            disabled={regenerating}
            className="text-[10px] underline hover:no-underline disabled:opacity-50"
            title="If this has been stuck for a while, click to retry"
          >
            {regenerating ? "Retrying…" : "Retry"}
          </button>
        </div>
      );
    }

    if (state === "error") {
      return (
        <div className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-xs text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
          <div className="flex items-start justify-between gap-2">
            <p className="font-medium">Analysis failed</p>
            <button
              type="button"
              onClick={() => handleRegenerate(step.id)}
              disabled={regenerating}
              className="text-[10px] underline hover:no-underline disabled:opacity-50"
            >
              {regenerating ? "Retrying…" : "Retry"}
            </button>
          </div>
          {version?.generationError && (
            <p className="mt-1 text-[10px]">{version.generationError}</p>
          )}
        </div>
      );
    }

    if (state !== "draft" || !version) return null;

    const areas = content.areas_to_probe ?? [];
    const gaps = content.gaps ?? [];

    return (
      <div className="mt-3 rounded border border-purple-200 bg-purple-50/60 p-3 dark:border-purple-900/40 dark:bg-purple-900/10">
        <div className="mb-1 flex items-center justify-between gap-2">
          <h5 className="text-[10px] font-semibold uppercase tracking-wide text-purple-700 dark:text-purple-300">
            Round analysis (v{version.versionNumber})
          </h5>
          <div className="flex items-center gap-2">
            {regenerateErrors[step.id] && (
              <span className="text-[10px] text-red-600 dark:text-red-400">
                {regenerateErrors[step.id]}
              </span>
            )}
            <HowDeterminedButton
              campaignCandidateId={cc.id}
              candidateName={cc.candidate.fullName}
              subject={{ kind: "synthesis", candidateSelectionStepId: step.id }}
              className="text-[10px] text-purple-700 underline decoration-dotted underline-offset-2 hover:text-purple-900 dark:text-purple-300 dark:hover:text-purple-200"
            />
            <button
              type="button"
              aria-label="Regenerate analysis"
              title="Regenerate analysis from current contributions"
              onClick={() => handleRegenerate(step.id)}
              disabled={regenerating}
              className="rounded p-1 text-purple-700 hover:bg-purple-100 disabled:opacity-50 dark:text-purple-300 dark:hover:bg-purple-900/40"
            >
              <RefreshCw size={12} className={regenerating ? "animate-spin" : undefined} />
            </button>
          </div>
        </div>
        {isStale && (
          <div
            className="mb-2 flex flex-wrap items-center gap-2 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300"
            data-testid="synthesis-stale-banner"
          >
            <span>
              Out of date — {staleCount} new feedback item{staleCount === 1 ? "" : "s"} since this
              analysis was generated.
            </span>
            <button
              type="button"
              onClick={() => handleRegenerate(step.id)}
              disabled={regenerating}
              className="underline hover:no-underline disabled:opacity-50"
            >
              {regenerating ? "Regenerating…" : "Regenerate to refresh"}
            </button>
          </div>
        )}
        {scoredCells.length > 0 && (
          <div className="mb-2" data-testid={`synthesis-scored-grid-${step.id}`}>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
              Scored dimensions
            </p>
            <ul className="mt-1 divide-y divide-purple-100 dark:divide-purple-900/30">
              {scoredCells.map((cell) => (
                <li
                  key={cell.id}
                  className="flex flex-wrap items-start gap-2 py-1"
                  data-testid={`synthesis-scored-cell-${cell.capabilityDimensionId}`}
                >
                  <span className="min-w-[10rem] text-xs font-medium text-text-primary">
                    {cell.dimensionName ?? "(dimension)"}
                  </span>
                  <span
                    className={`rounded-full bg-card-alt px-2 py-0.5 text-[10px] font-semibold ${RATING_COLORS[cell.score] ?? "text-text-muted"}`}
                  >
                    {cell.score}/5{cell.anchorLabel ? ` — ${cell.anchorLabel}` : ""}
                  </span>
                  <span className="flex-1 text-[11px] text-text-secondary">{cell.evidence}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {content.round_summary && (
          <p className="text-xs text-text-secondary">{content.round_summary}</p>
        )}
        {gaps.length > 0 && (
          <div
            className="mt-2 rounded border border-amber-200 bg-amber-50/60 p-2 dark:border-amber-900/40 dark:bg-amber-900/10"
            data-testid={`synthesis-gaps-${step.id}`}
          >
            <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300">
              Coverage gaps
            </p>
            <ul className="mt-1 space-y-1 text-xs text-text-secondary">
              {gaps.map((gap, idx) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: list is stable per version
                <li key={`gap-${idx}`}>
                  <span className="font-medium text-text-primary">
                    {gap.dimension_name ?? "(dimension)"}
                  </span>
                  {gap.note && <span> — {gap.note}</span>}
                  {(gap.follow_up_questions ?? []).length > 0 && (
                    <ul className="mt-0.5 list-disc space-y-0.5 pl-4 text-[11px] text-text-muted">
                      {(gap.follow_up_questions ?? []).map((question, qIdx) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: list is stable per version
                        <li key={`gap-${idx}-q-${qIdx}`}>{question}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {areas.length > 0 && (
          <div className="mt-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
              Areas to probe next round
            </p>
            <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-text-secondary">
              {areas.map((area, idx) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: list is stable per version
                <li key={`area-${idx}`}>
                  <span className="font-medium">{area.topic}</span>
                  {area.rationale && <span className="text-text-muted"> — {area.rationale}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {synthesis.totalVersions > 1 && renderVersionHistory(synthesis)}
      </div>
    );
  };

  const renderVersionHistory = (synthesis: InterviewRoundSynthesisData) => {
    const open = versionHistoryOpen[synthesis.id] === true;
    const versions = synthesis.versions ?? [];
    return (
      <div className="mt-2 border-t border-purple-200 pt-1 dark:border-purple-900/40">
        <button
          type="button"
          data-testid={`synthesis-version-history-toggle-${synthesis.id}`}
          onClick={() => setVersionHistoryOpen((prev) => ({ ...prev, [synthesis.id]: !open }))}
          className="text-[10px] text-text-muted underline hover:no-underline"
        >
          {open ? "Hide" : "Show"} {synthesis.totalVersions} version
          {synthesis.totalVersions === 1 ? "" : "s"}
        </button>
        {open && (
          <ol className="mt-1 space-y-1" data-testid={`synthesis-version-history-${synthesis.id}`}>
            {versions.map((v) => (
              <li
                key={v.id}
                className="flex flex-wrap items-baseline gap-2 text-[10px] text-text-muted"
              >
                <span className="font-medium text-text-secondary">v{v.versionNumber}</span>
                <span>{new Date(v.effectiveFrom).toLocaleString()}</span>
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[9px] font-medium ${
                    v.origin === "user"
                      ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
                      : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                  }`}
                >
                  {v.origin ?? "system"}
                </span>
                <span>{v.author?.name}</span>
                {v.changeSummary && <span className="italic">— {v.changeSummary}</span>}
              </li>
            ))}
          </ol>
        )}
      </div>
    );
  };

  const renderStep = (
    step: CandidateSelectionStepData,
    isCurrent: boolean,
    cc: CampaignCandidateData,
    movedPast: boolean,
    stepContributions: InterviewContributionData[],
  ) => {
    const ccId = cc.id;
    const draft = recordDraft[ccId] ?? { outcome: "advance", rating: "" };
    const setDraft = (patch: Partial<{ outcome: string; rating: string }>) =>
      setRecordDraft((prev) => ({ ...prev, [ccId]: { ...draft, ...patch } }));
    const feedbackOpen = feedbackStepId === step.id;

    return (
      <div
        key={step.id}
        className={`rounded border p-3 ${
          isCurrent ? "border-accent/50 bg-accent/5" : "border-border bg-card-alt"
        }`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-text-faint">{step.round}</span>
          <span className="text-xs font-medium text-text-primary">{stepDisplayLabel(step)}</span>
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_COLORS[step.status] ?? ""}`}
          >
            {step.status}
          </span>
          {step.outcome !== "pending" && (
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${OUTCOME_COLORS[step.outcome] ?? ""}`}
            >
              {step.outcome}
            </span>
          )}
          {step.rating != null && (
            <span className={`text-xs font-medium ${RATING_COLORS[step.rating] ?? ""}`}>
              {step.rating}/5
            </span>
          )}
          {step.amended && (
            <button
              type="button"
              data-testid={`amended-badge-${step.id}`}
              onClick={() =>
                setAmendmentHistoryOpen((prev) => ({ ...prev, [step.id]: !prev[step.id] }))
              }
              className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600 underline decoration-dotted hover:text-slate-800 dark:bg-slate-800 dark:text-slate-300 dark:hover:text-slate-100"
            >
              Amended
            </button>
          )}
          {!isCurrent && step.status === "completed" && (
            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => openAmendDialog(step, cc)}
                data-testid={`amend-step-${step.id}`}
              >
                <Pencil size={12} /> Amend outcome
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleReopenStep(step)}
                disabled={reopening}
                data-testid={`reopen-step-${step.id}`}
              >
                <RotateCcw size={12} /> {reopening ? "Reopening…" : "Reopen step"}
              </Button>
            </div>
          )}
        </div>
        {!isCurrent && step.status === "completed" && (
          <p className="mt-1 text-[10px] text-text-faint">
            Amend outcome corrects the decision in place. Reopen step lets you add more feedback and
            record a new outcome. Neither affects later steps.
          </p>
        )}
        {step.amended && amendmentHistoryOpen[step.id] && (
          <ol className="mt-2 space-y-1" data-testid={`amendment-history-${step.id}`}>
            {step.amendments.map((a) => {
              if (a.eventType === "reopen") {
                return (
                  <li
                    key={a.id}
                    className="flex flex-wrap items-baseline gap-1 text-[10px] text-text-muted"
                  >
                    <span className="font-medium text-text-secondary">
                      {a.author?.name ?? "Unknown"}
                    </span>
                    <span>
                      reopened this step (was: {a.fromOutcome}
                      {a.fromRating != null ? ` ${a.fromRating}/5` : ""})
                    </span>
                    {a.reason && <span className="italic">— {a.reason}</span>}
                    <span className="text-text-faint">
                      ({new Date(a.createdAt).toLocaleString()})
                    </span>
                  </li>
                );
              }
              const changes: string[] = [];
              if (a.fromOutcome !== a.toOutcome) {
                changes.push(`outcome ${a.fromOutcome} → ${a.toOutcome}`);
              }
              if (a.fromRating !== a.toRating) {
                changes.push(`rating ${a.fromRating ?? "—"} → ${a.toRating ?? "—"}`);
              }
              return (
                <li
                  key={a.id}
                  className="flex flex-wrap items-baseline gap-1 text-[10px] text-text-muted"
                >
                  <span className="font-medium text-text-secondary">
                    {a.author?.name ?? "Unknown"}
                  </span>
                  <span>changed {changes.join(", ")}</span>
                  {a.reason && <span className="italic">— {a.reason}</span>}
                  <span className="text-text-faint">
                    ({new Date(a.createdAt).toLocaleString()})
                  </span>
                </li>
              );
            })}
          </ol>
        )}
        {step.outcomeContradictsLaterActivity && !dismissedContradictions.has(step.id) && (
          <div
            className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
            data-testid={`outcome-contradiction-${step.id}`}
          >
            <div className="flex items-start gap-1.5">
              <AlertTriangle size={14} className="mt-px shrink-0" />
              <span>
                This step's outcome ({step.outcome}) conflicts with activity on later steps.
              </span>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => openAmendDialog(step, cc, "advance")}
                data-testid={`contradiction-resolve-${step.id}`}
              >
                Change back to advance
              </Button>
              <button
                type="button"
                onClick={() => setDismissedContradictions((prev) => new Set([...prev, step.id]))}
                className="text-xs text-amber-700 underline hover:text-amber-900 dark:text-amber-400 dark:hover:text-amber-200"
                data-testid={`contradiction-dismiss-${step.id}`}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {step.interviewPackEligible && (
          <PreInterviewNotesEditor
            stepId={step.id}
            initialNotes={step.candidatePrepNotes}
            disabled={movedPast}
          />
        )}

        {step.assessmentFormEligible && (
          <AssessmentFormPanel
            candidateSelectionStepId={step.id}
            form={step.assessmentForm}
            onImported={refresh}
          />
        )}

        {step.interviewPackEligible && (
          <InterviewPackPanel
            candidateSelectionStepId={step.id}
            pack={step.interviewPack}
            // Story #2255 — Regenerate stays available even on moved-past
            // steps so late-arriving data (assessment scored after the pack
            // was first composed) can be folded into a fresh snapshot.
            // Pack versions are append-only; the prior version stays in
            // history, so refreshing a historical step is non-destructive.
            onChanged={refresh}
          />
        )}

        {renderSynthesis(step, cc, stepContributions)}
        {renderGenerateSynthesisCTA(step, stepContributions)}

        {/* Per-step feedback (note / paste transcript / upload). */}
        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between">
            <h5 className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
              <ClipboardList size={10} /> Feedback ({stepContributions.length})
            </h5>
            {/* Completed steps stay open for late feedback even after the
                candidate moves past — the synthesis goes stale and offers
                regeneration via the existing banner. Never-reached steps of
                moved-past candidates stay gated (they're pending). */}
            {(!movedPast || step.status === "completed") && (
              <button
                type="button"
                onClick={() => openFeedbackFor(step.id)}
                className="flex items-center gap-1 text-[10px] text-accent hover:underline"
              >
                <Plus size={10} /> {feedbackOpen ? "Close" : "Add feedback"}
              </button>
            )}
          </div>

          {(!movedPast || step.status === "completed") &&
            feedbackOpen &&
            renderFeedbackForm(ccId, step.id)}

          {stepContributions.length === 0 && !feedbackOpen ? (
            <p className="text-[10px] text-text-faint">No feedback yet for this step.</p>
          ) : (
            stepContributions.map(renderContribution)
          )}
        </div>

        {isCurrent && (
          <div className="mt-3 border-t border-border pt-3">
            <h5 className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
              Record outcome
            </h5>
            <p className="mt-1 text-[10px] text-text-faint">
              The expected flow is{" "}
              <span className="font-medium text-text-muted">add feedback above first</span>, then
              record the outcome here to close this step and advance to the next.
            </p>
            {stepContributions.length === 0 && (
              <p className="mt-1 text-[10px] font-medium text-amber-700 dark:text-amber-400">
                No feedback added yet — recording now will ask you to confirm.
              </p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-1 text-[10px] text-text-muted">
                Outcome
                <select
                  aria-label="Outcome"
                  value={draft.outcome}
                  onChange={(e) => setDraft({ outcome: e.target.value })}
                  className="rounded border border-border bg-surface px-2 py-1 text-xs"
                >
                  {ADVANCE_OUTCOMES.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-1 text-[10px] text-text-muted">
                Rating
                <select
                  aria-label="Rating"
                  value={draft.rating}
                  onChange={(e) => setDraft({ rating: e.target.value })}
                  className="rounded border border-border bg-surface px-2 py-1 text-xs"
                >
                  <option value="">—</option>
                  {[1, 2, 3, 4, 5].map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={() => requestAdvance(ccId, step, stepContributions)}
                disabled={advancing}
                className="rounded bg-accent px-3 py-1 text-xs text-white hover:bg-accent/90 disabled:opacity-50"
              >
                {advancing ? "Recording..." : "Record"}
              </button>
              <InfoTooltip
                label="What Record does"
                content={
                  <span>
                    <strong>Records</strong> the outcome of this interview step (advance, hold, or
                    reject). The most recent analysis is there to inform your decision — if it is
                    out of date or hasn't been generated, you'll be prompted to generate it first.
                  </span>
                }
              />
              {recordWithoutFeedbackFor === ccId && renderRecordWithoutFeedbackConfirm(cc, step)}
              {assessmentFallbackFor === ccId && renderAssessmentFallbackConfirm(cc, step)}
            </div>
          </div>
        )}
      </div>
    );
  };

  // A step after the halt (#3168): kept (amending the halt to advance reopens
  // it) but never reached, so no prep notes / pack / feedback / Record panels.
  const renderNotReachedStep = (step: CandidateSelectionStepData) => (
    <div
      key={step.id}
      data-testid={`step-not-reached-${step.id}`}
      className="flex items-center gap-2 rounded border border-dashed border-border bg-card-alt px-3 py-2 opacity-60"
    >
      <span className="text-xs text-text-faint">{step.round}</span>
      <span className="text-xs font-medium text-text-muted">{stepDisplayLabel(step)}</span>
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
        Not reached
      </span>
    </div>
  );

  // Rendered directly under the halting step in place of the next step's
  // Record panel: names the halt and offers the two ways forward.
  const renderJourneyHaltBanner = (cc: CampaignCandidateData, halt: JourneyHalt) => {
    const isHold = halt.reason === "hold";
    const confirm = rejectFromCampaign?.ccId === cc.id ? rejectFromCampaign : null;
    return (
      <div
        key={`halt-${cc.id}`}
        data-testid={`journey-halt-${cc.id}`}
        className={`rounded border p-3 text-xs ${
          isHold
            ? "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
            : "border-red-300 bg-red-50 text-red-800 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300"
        }`}
      >
        <div className="flex items-start gap-1.5">
          {isHold ? (
            <PauseCircle size={14} className="mt-px shrink-0" />
          ) : (
            <Ban size={14} className="mt-px shrink-0" />
          )}
          <div>
            <p className="font-medium">{haltHeading(halt)}</p>
            <p className="mt-0.5 opacity-80">
              The journey has stopped here — later steps are not reached.{" "}
              {isHold
                ? "Resume to reopen the next step, or reject the candidate from the campaign."
                : "Reject the candidate from the campaign to move them out of the live pipeline, or reconsider to reopen the next step."}
            </p>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => openRejectFromCampaign(cc, halt.step)}
            data-testid={`reject-from-campaign-${cc.id}`}
          >
            Reject from campaign
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => openAmendDialog(halt.step, cc, "advance")}
            data-testid={`reconsider-step-${halt.step.id}`}
          >
            {isHold ? "Resume" : "Reconsider"}
          </Button>
        </div>
        {confirm && (
          <div
            className="mt-2 space-y-2 rounded border border-border bg-card p-2 text-text-secondary"
            data-testid={`reject-from-campaign-confirm-${cc.id}`}
          >
            <label className="block text-[10px] text-text-muted">
              Reason (kept on the stage change)
              <textarea
                aria-label="Rejection reason"
                value={confirm.reason}
                onChange={(e) =>
                  setRejectFromCampaign((prev) => prev && { ...prev, reason: e.target.value })
                }
                rows={3}
                className="mt-1 w-full rounded border border-border bg-surface px-2 py-1 text-xs text-text-primary"
              />
            </label>
            {confirm.error && (
              <p
                className="text-xs text-red-600 dark:text-red-400"
                data-testid={`reject-from-campaign-error-${cc.id}`}
              >
                {confirm.error}
              </p>
            )}
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => void submitRejectFromCampaign()}
                data-testid={`reject-from-campaign-confirm-button-${cc.id}`}
              >
                Confirm rejection
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setRejectFromCampaign(null)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Journey rows for a candidate: full step cards up to the halt, the halt
  // banner (live candidates only), then compact "Not reached" rows.
  const renderJourneySteps = (
    cc: CampaignCandidateData,
    steps: CandidateSelectionStepData[],
    movedPast: boolean,
    contributionsForStep: (stepId: string) => InterviewContributionData[],
  ) => {
    const current = currentStepOf(cc);
    const halt = haltOf(cc);
    return steps.map((step) => {
      const notReached = halt !== null && (step.round ?? 0) > (halt.step.round ?? 0);
      if (notReached) return renderNotReachedStep(step);
      return (
        <Fragment key={step.id}>
          {renderStep(
            step,
            !movedPast && current?.id === step.id,
            cc,
            movedPast,
            contributionsForStep(step.id),
          )}
          {halt && !movedPast && halt.step.id === step.id && renderJourneyHaltBanner(cc, halt)}
        </Fragment>
      );
    });
  };

  const renderCandidate = (cc: CampaignCandidateData, movedPast: boolean) => {
    const isExpanded = expandedId === cc.id;
    const steps = orderedSteps(cc);
    const current = currentStepOf(cc);
    const halt = haltOf(cc);
    const completedCount = steps.filter((s) => s.status === "completed").length;
    // Step-less contributions (legacy notes from before per-step pinning) get
    // their own group below the per-step lists so they remain visible.
    const generalContributions = cc.interviewContributions.filter(
      (c) => !c.candidateSelectionStepId,
    );
    const contributionsForStep = (stepId: string) =>
      cc.interviewContributions.filter((c) => c.candidateSelectionStepId === stepId);

    return (
      <div key={cc.id} className="border-b border-border">
        <div className="flex items-center gap-3 px-3 py-2">
          <button
            type="button"
            aria-label={`Toggle ${cc.candidate.fullName}`}
            onClick={() => setExpandedId(isExpanded ? null : cc.id)}
            className="rounded p-0.5 text-text-faint hover:bg-card-hover"
          >
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          <div className="min-w-0 flex-1">
            {typeof cc.candidate.referenceNumber === "number" && (
              <>
                <CandidateReferenceBadge referenceNumber={cc.candidate.referenceNumber} />{" "}
              </>
            )}
            <span className="text-sm font-medium text-text-primary">{cc.candidate.fullName}</span>
            {(cc.candidate.currentRoleTitle || cc.candidate.currentCompany) && (
              <span className="ml-2 text-xs text-text-muted">
                {[cc.candidate.currentRoleTitle, cc.candidate.currentCompany]
                  .filter(Boolean)
                  .join(" at ")}
              </span>
            )}
          </div>
          {halt ? (
            <span
              className={`text-xs font-medium ${
                halt.reason === "hold"
                  ? "text-amber-700 dark:text-amber-400"
                  : "text-red-700 dark:text-red-400"
              }`}
              data-testid={`journey-halt-summary-${cc.id}`}
            >
              {haltHeading(halt)}
            </span>
          ) : current ? (
            <span className="text-xs text-text-faint">Current: {stepDisplayLabel(current)}</span>
          ) : (
            steps.length > 0 && <span className="text-xs text-text-faint">All steps complete</span>
          )}
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 dark:bg-gray-800 dark:text-gray-300">
            {completedCount}/{steps.length} steps
          </span>
          {steps.some((s) => s.outcomeContradictsLaterActivity) && (
            <span
              title="A completed step's outcome contradicts later activity — expand to review"
              data-testid={`contradiction-flag-${cc.id}`}
            >
              <AlertTriangle size={12} className="text-amber-500" />
            </span>
          )}
          {movedPast && (
            <div className="flex items-center gap-1">
              <StageBadge stage={cc.stage} />
              <TerminalStageMoveControl
                stage={cc.stage}
                onMove={(stage) => handleMoveStage(cc.id, stage)}
              />
            </div>
          )}
        </div>

        {isExpanded && (
          <div className="mx-10 mb-3 space-y-3">
            {/* Selection-step journey (feedback intake lives inline per step) */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                  <ListChecks size={10} /> Selection Steps
                </h4>
              </div>
              {steps.length === 0 ? (
                <p className="text-xs text-text-faint">
                  {processSteps.length > 0
                    ? "No selection steps yet."
                    : "No selection steps yet. Define the selection process to add steps for each candidate."}
                </p>
              ) : (
                <div className="space-y-2">
                  {renderJourneySteps(cc, steps, movedPast, contributionsForStep)}
                </div>
              )}
            </div>

            {/* General feedback — step-less contributions (legacy / pre-step). */}
            {generalContributions.length > 0 && (
              <div className="space-y-2">
                <h4 className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                  <ClipboardList size={10} /> General feedback
                </h4>
                {generalContributions.map(renderContribution)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Per-step tab body: any candidate (active or moved-on) who has a step row
  // matching the active tab's continuity, rendered flat (candidate header +
  // that specific step row). A candidate appears in every step tab they've
  // touched, so prior-round state and feedback stay visible after advancing.
  // Reuses renderStep so pack / synthesis / feedback / record controls all
  // continue to work without duplication; renderStep itself decides what to
  // show based on isCurrent and movedPast.
  const renderCandidateAtStep = (
    cc: CampaignCandidateData,
    step: CandidateSelectionStepData,
    movedPast: boolean,
  ) => {
    const steps = orderedSteps(cc);
    const completedCount = steps.filter((s) => s.status === "completed").length;
    const halt = haltOf(cc);
    const isCurrent = !movedPast && currentStepOf(cc)?.id === step.id;
    const notReached = halt !== null && (step.round ?? 0) > (halt.step.round ?? 0);
    const stepContributions = cc.interviewContributions.filter(
      (c) => c.candidateSelectionStepId === step.id,
    );

    return (
      <div key={`${cc.id}-${step.id}`} className="border-b border-border">
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="min-w-0 flex-1">
            {typeof cc.candidate.referenceNumber === "number" && (
              <>
                <CandidateReferenceBadge referenceNumber={cc.candidate.referenceNumber} />{" "}
              </>
            )}
            <span className="text-sm font-medium text-text-primary">{cc.candidate.fullName}</span>
            {(cc.candidate.currentRoleTitle || cc.candidate.currentCompany) && (
              <span className="ml-2 text-xs text-text-muted">
                {[cc.candidate.currentRoleTitle, cc.candidate.currentCompany]
                  .filter(Boolean)
                  .join(" at ")}
              </span>
            )}
          </div>
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 dark:bg-gray-800 dark:text-gray-300">
            {completedCount}/{steps.length} steps
          </span>
          {movedPast && (
            <div className="flex items-center gap-1">
              <StageBadge stage={cc.stage} />
              <TerminalStageMoveControl
                stage={cc.stage}
                onMove={(stage) => handleMoveStage(cc.id, stage)}
              />
            </div>
          )}
        </div>
        <div className="mx-10 mb-3 space-y-2">
          {notReached
            ? renderNotReachedStep(step)
            : renderStep(step, isCurrent, cc, movedPast, stepContributions)}
          {halt && !movedPast && halt.step.id === step.id && renderJourneyHaltBanner(cc, halt)}
        </div>
      </div>
    );
  };

  // Active step tab metadata (null when the active tab is "all").
  const activeStepTab =
    activeTab === ALL_TAB ? null : (stepTabs.find((s) => s.continuityId === activeTab) ?? null);

  // Temporary guide mapping until #2252 lands: 1st interview-flavoured tab
  // Story #2252: each interview-flavoured step has its own InterviewGuide,
  // resolved here by the current step's id (the GraphQL field exposes the
  // currently-effective SelectionStep on the guide's continuity).
  const guideForActiveStep = (): {
    stepId: string;
    stepType: string;
    guide: InterviewGuideData | null;
  } | null => {
    if (!activeStepTab) return null;
    const stepRow = stepTabs.find((s) => s.continuityId === activeStepTab.continuityId);
    if (!stepRow) return null;
    const guide = interviewGuides.find((g) => g.selectionStepId === stepRow.id) ?? null;
    return { stepId: stepRow.id, stepType: stepRow.stepType ?? "interview", guide };
  };

  // Step-history-aware tab population: every (candidate, step) pair whose
  // continuity matches the tab, regardless of whether that step row is
  // currently active or already completed. Active rows surface first; among
  // completed rows, most-recently-updated first so the latest transitions
  // stay near the top.
  const stepRowsAt = (continuityId: string) => {
    const rows: Array<{
      cc: CampaignCandidateData;
      step: CandidateSelectionStepData;
      movedPast: boolean;
    }> = [];
    for (const cc of allCandidates) {
      const movedPast = cc.stage !== "interview";
      for (const step of cc.candidateSelectionSteps) {
        if (step.continuityId === continuityId) {
          rows.push({ cc, step, movedPast });
        }
      }
    }
    rows.sort((a, b) => {
      const aActive = a.step.status !== "completed";
      const bActive = b.step.status !== "completed";
      if (aActive !== bActive) return aActive ? -1 : 1;
      if (!aActive) {
        return (b.step.updatedAt ?? "").localeCompare(a.step.updatedAt ?? "");
      }
      return a.cc.candidate.fullName.localeCompare(b.cc.candidate.fullName);
    });
    return rows;
  };

  return (
    <TooltipProvider>
      <div className="space-y-4">
        <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
          <div className="mb-3 flex items-center gap-2">
            <UserCheck size={16} className="text-text-faint" />
            <h3 className="text-sm font-medium text-text-secondary">Interviews</h3>
            <span className="text-xs text-text-faint">
              {activeCandidates.length} active
              {movedOnCandidates.length > 0 && `, ${movedOnCandidates.length} moved on`}
            </span>
          </div>

          {/* Sub-tab strip: "All candidates" + one per interview-flavoured step. */}
          <nav
            aria-label="Interview sub-tabs"
            className="mb-3 flex flex-wrap gap-1 border-b border-border"
          >
            <button
              type="button"
              data-testid="interviews-subtab-all"
              onClick={() => setActiveTab(ALL_TAB)}
              className={`-mb-px border-b-2 px-3 py-2 text-xs font-medium transition-colors ${
                activeTab === ALL_TAB
                  ? "border-accent text-text-primary"
                  : "border-transparent text-text-muted hover:text-text-secondary"
              }`}
            >
              All candidates
            </button>
            {stepTabs.map((s) => {
              const active = activeTab === s.continuityId;
              return (
                <button
                  key={s.continuityId}
                  type="button"
                  data-testid={`interviews-subtab-${s.continuityId}`}
                  onClick={() => setActiveTab(s.continuityId)}
                  className={`-mb-px border-b-2 px-3 py-2 text-xs font-medium transition-colors ${
                    active
                      ? "border-accent text-text-primary"
                      : "border-transparent text-text-muted hover:text-text-secondary"
                  }`}
                >
                  {stepDisplayLabel(s)}
                </button>
              );
            })}
          </nav>

          {activeTab === ALL_TAB ? (
            allCandidates.length === 0 ? (
              <p className="py-6 text-center text-sm text-text-faint">
                No candidates at interview stage. Move candidates from the Shortlist.
              </p>
            ) : (
              <>
                <InterviewsOverviewMatrix
                  campaignCandidates={allCandidates}
                  steps={stepTabs}
                  onJumpToStep={setActiveTab}
                />
                <div className="space-y-1">
                  {activeCandidates.map((cc) => renderCandidate(cc, false))}
                  {movedOnCandidates.map((cc) => renderCandidate(cc, true))}
                </div>
              </>
            )
          ) : (
            <div className="space-y-3" data-testid="interviews-subtab-step-body">
              {(() => {
                const guideMapping = guideForActiveStep();
                if (guideMapping) {
                  return (
                    <InterviewGuidePanel
                      campaignId={campaignId}
                      selectionStepId={guideMapping.stepId}
                      stepType={guideMapping.stepType}
                      guide={guideMapping.guide}
                      blueprintLocked={blueprintLocked}
                      onSaved={refresh}
                      defaultCollapsed
                    />
                  );
                }
                return (
                  <p
                    className="rounded border border-dashed border-border px-3 py-2 text-xs text-text-faint"
                    data-testid="interviews-subtab-guide-placeholder"
                  >
                    This step has no Interview Guide yet.
                  </p>
                );
              })()}
              {activeStepTab &&
                (() => {
                  const rows = stepRowsAt(activeStepTab.continuityId);
                  if (rows.length === 0) {
                    return (
                      <p className="py-6 text-center text-sm text-text-faint">
                        No candidates have reached this step yet.
                      </p>
                    );
                  }
                  return (
                    <div className="space-y-1">
                      {rows.map((r) => renderCandidateAtStep(r.cc, r.step, r.movedPast))}
                    </div>
                  );
                })()}
            </div>
          )}
        </div>
        {amendDialog &&
          (() => {
            const amendCc = allCandidates.find((cc) => cc.id === amendDialog.ccId);
            const laterStepsHaveActivity = amendCc
              ? orderedSteps(amendCc).some(
                  (s) => (s.round ?? 0) > (amendDialog.stepRound ?? 0) && s.status !== "pending",
                )
              : false;
            const wouldContradict =
              laterStepsHaveActivity &&
              (amendDialog.outcome === "reject" || amendDialog.outcome === "hold");
            return (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center"
                data-testid="amend-outcome-dialog"
              >
                {/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop dismiss overlay */}
                <div
                  className="absolute inset-0 bg-black/30"
                  onClick={() => setAmendDialog(null)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setAmendDialog(null);
                  }}
                />
                <div className="relative flex w-full max-w-md flex-col rounded-lg bg-card shadow-xl">
                  <div className="flex items-start justify-between border-b border-border px-6 py-4">
                    <h2 className="text-sm font-semibold text-text-primary">
                      Amend outcome — {amendDialog.stepLabel}
                    </h2>
                    <button
                      type="button"
                      onClick={() => setAmendDialog(null)}
                      className="rounded p-1 text-text-faint hover:bg-card-hover hover:text-text-secondary"
                      aria-label="Close dialog"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <div className="flex-1 space-y-3 px-6 py-4 text-sm text-text-secondary">
                    <p className="text-xs text-text-muted">
                      Records a correction to this completed step. Later steps are not affected and
                      the change is kept in the step's amendment history.
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                      <label className="flex items-center gap-1 text-xs text-text-muted">
                        Outcome
                        <select
                          aria-label="Amended outcome"
                          value={amendDialog.outcome}
                          onChange={(e) =>
                            setAmendDialog((prev) => prev && { ...prev, outcome: e.target.value })
                          }
                          className="rounded border border-border bg-surface px-2 py-1 text-xs"
                        >
                          {ADVANCE_OUTCOMES.map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="flex items-center gap-1 text-xs text-text-muted">
                        Rating
                        <select
                          aria-label="Amended rating"
                          value={amendDialog.rating}
                          onChange={(e) =>
                            setAmendDialog((prev) => prev && { ...prev, rating: e.target.value })
                          }
                          className="rounded border border-border bg-surface px-2 py-1 text-xs"
                        >
                          <option value="">—</option>
                          {[1, 2, 3, 4, 5].map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <label className="block text-xs text-text-muted">
                      Reason
                      <textarea
                        value={amendDialog.reason}
                        onChange={(e) =>
                          setAmendDialog((prev) => prev && { ...prev, reason: e.target.value })
                        }
                        rows={2}
                        placeholder="Why is this being corrected? (kept in the amendment history)"
                        className="mt-1 w-full resize-y rounded border border-border bg-surface px-2 py-1 text-xs text-text-primary"
                        data-testid="amend-reason"
                      />
                    </label>
                    {wouldContradict && (
                      <div className="flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                        <AlertTriangle size={12} className="mt-px shrink-0" />
                        <span>
                          Changing to {amendDialog.outcome} will create a contradiction — later
                          steps already have activity recorded. You can resolve this after saving.
                        </span>
                      </div>
                    )}
                    {amendDialog.error && (
                      <p
                        className="text-xs text-red-600 dark:text-red-400"
                        data-testid="amend-error"
                      >
                        {amendDialog.error}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
                    <Button
                      variant="secondary"
                      onClick={() => setAmendDialog(null)}
                      data-testid="amend-cancel"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => void submitAmend()}
                      disabled={amending}
                      data-testid="amend-save"
                    >
                      {amending ? "Saving…" : "Save correction"}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })()}
        <RegenerateConfirmDialog
          open={regenerateConfirm !== null}
          warning={regenerateConfirm?.warning ?? ""}
          deepLinkPath={regenerateConfirm?.deepLinkPath}
          deepLinkLabel={regenerateConfirm?.deepLinkLabel}
          onCancel={() => setRegenerateConfirm(null)}
          onConfirm={() => {
            const stepId = regenerateConfirm?.stepId;
            setRegenerateConfirm(null);
            if (stepId) {
              void runRegenerate(stepId, true);
            }
          }}
        />
        {viewer && (
          <ContentViewerOverlay
            open={true}
            onOpenChange={(o) => {
              if (!o) setViewer(null);
            }}
            title={viewer.title}
            format={viewer.format}
            content={viewer.content}
            downloadFilename={viewer.downloadFilename}
          />
        )}
      </div>
    </TooltipProvider>
  );
}
